import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type MouseEvent } from 'react';
import {
  Badge,
  Card,
  cardClasses,
  cn,
  Field,
  Icon,
  linkTextClasses,
  Page,
  PageHeader,
  type IconName,
} from '@evogard/ui';
import {
  format,
  loadFailureMessage,
  parseMoneyInput,
  recurrencePlan,
  type Money,
  type RecurrencePlan,
  type RecurrencePlanMonth,
} from '@evogard/domain';
import type { WalletCalculator } from '@evogard/contract';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { periodLabel } from './common.js';
import { compactMoney } from './StatementChart.js';

/**
 * A CALCULADORA — `/sales/calculator` (29/10/2026).
 *
 * Saiu das Métricas e virou página, no desenho de `referencia/calculadora.jpeg`.
 * As Métricas respondem "o que a minha carteira é"; esta responde "quanto
 * preciso vender para chegar lá". A conta é `recurrencePlan`, do domínio, e o
 * teste dela mora lá.
 *
 * A meta, o ticket e a comissão nascem vazios: a tela não escolhe a meta de
 * ninguém, e um número de exemplo preenchido viraria o resultado de quem não
 * reparou que ele estava ali.
 *
 * ## As vendas e o churn nascem MEDIDOS (29/10/2026, pedido do dono)
 *
 * Sem as vendas, "Meses para a meta" fica em "—" mesmo com os outros três
 * preenchidos. Os dois padrões saem da carteira de quem abre, nos três meses
 * fechados (`walletCalculator`): a média de "Novas Vendas" do BI, e os
 * cancelados ÷ a carteira do primeiro dia de cada mês. São números da
 * pessoa, e não exemplos — a ajuda de cada campo diz de onde vieram. Digitar
 * por cima vale; apagar deixa vazio; "usar o medido" volta. Sem vínculo com o
 * ERP, média zero ou carteira vazia, o campo fica vazio como os outros.
 */
export function CalculatorScreen() {
  const [targetText, setTargetText] = useState('');
  const [ticketText, setTicketText] = useState('');
  const [commissionText, setCommissionText] = useState('');
  /* `null` é "a pessoa não digitou": o campo mostra o número medido. */
  const [typedSales, setTypedSales] = useState<string | null>(null);
  const [typedChurn, setTypedChurn] = useState<string | null>(null);

  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const baselineQuery = useQuery({
    queryKey: ['sales', 'wallet', 'calculator', tenantId],
    queryFn: async () => {
      const r = await api.sales.walletCalculator({ query: {} });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'as suas vendas e o seu churn'));
      }
      return r.body;
    },
    enabled: tenantId !== undefined,
    staleTime: BASELINE_STALE_MS,
  });
  const baseline = measuredBaseline(baselineQuery.data);
  const measuredSales =
    baseline !== null && baseline.salesAverage > 0 ? baseline.salesAverage : null;
  const measuredChurn = baseline?.churnPercent ?? null;
  const salesText = typedSales ?? (measuredSales === null ? '' : String(measuredSales));
  const churnText =
    typedChurn ?? (measuredChurn === null ? '' : decimalText(measuredChurn));

  const target = parseMoneyInput(targetText);
  const ticket = parseMoneyInput(ticketText);
  const commission = parsePercent(commissionText);
  const sales = /^\d+$/.test(salesText.trim()) ? Number(salesText.trim()) : null;
  const churn = churnText.trim() === '' ? null : parsePercent(churnText);
  const churnInvalid = churnText.trim() !== '' && (churn === null || churn >= 100);

  const plan =
    target === null || ticket === null || commission === null || churnInvalid
      ? null
      : recurrencePlan({
          target,
          ticket,
          commissionPercent: commission,
          salesPerMonth: sales ?? 0,
          churnPercent: churn,
        });

  const moneyError = (text: string, parsed: Money | null) =>
    text.trim() !== '' && parsed === null ? 'Use o formato 1000 ou 1.000,00.' : undefined;
  const percentError = (text: string, parsed: number | null) =>
    text.trim() !== '' && parsed === null ? 'Use o formato 10 ou 7,5.' : undefined;

  return (
    <Page measure="cheia">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Icon nome="calculadora" size={24} className="text-fase-laranja" />
            Calculadora
          </span>
        }
      >
        Planeje sua recorrência — descubra quantas vendas você precisa para atingir sua
        meta mensal.
      </PageHeader>

      <section
        aria-labelledby="calculadora-parametros"
        className={cardClasses({ padding: false, className: 'rounded-2xl p-5' })}
      >
        <h2
          id="calculadora-parametros"
          className="mb-4 text-xs font-semibold uppercase tracking-[0.08em] text-texto-suave"
        >
          Parâmetros
        </h2>
        <div className="grid grid-cols-1 gap-x-4 gap-y-5 md:grid-cols-2">
          <Field
            large
            label="Meta de recorrência"
            help="Quanto quer receber por mês em recorrência"
            inputMode="decimal"
            prefix="R$"
            placeholder="1.000"
            value={targetText}
            onChange={(e) => setTargetText(e.target.value)}
            error={moneyError(targetText, target)}
          />
          <Field
            large
            label="Ticket médio"
            help="Valor médio mensal de cada apólice ativa"
            inputMode="decimal"
            prefix="R$"
            placeholder="147"
            value={ticketText}
            onChange={(e) => setTicketText(e.target.value)}
            error={moneyError(ticketText, ticket)}
          />
          <Field
            large
            label="Comissão da sua recorrência"
            help="% sobre o ticket médio que você recebe mensalmente"
            inputMode="decimal"
            suffix="%"
            placeholder="10"
            value={commissionText}
            onChange={(e) => setCommissionText(e.target.value)}
            error={percentError(commissionText, commission)}
          />
          <Field
            large
            label="Vendas por mês"
            help={
              baseline === null || measuredSales === null ? (
                'Quantas novas vendas você faz em média por mês'
              ) : (
                <MeasuredHelp
                  text={`${windowLabel(baseline)}: ${plural(measuredSales, 'venda', 'vendas')}/mês em média`}
                  edited={sales !== measuredSales}
                  onReset={() => setTypedSales(null)}
                />
              )
            }
            inputMode="numeric"
            suffix="vendas"
            placeholder="37"
            value={salesText}
            onChange={(e) => setTypedSales(e.target.value)}
            error={
              salesText.trim() !== '' && sales === null
                ? 'Use um número inteiro de vendas.'
                : undefined
            }
          />
          <Field
            large
            label="Churn mensal (opcional)"
            help={
              baseline === null || measuredChurn === null ? (
                '% de clientes que cancelam por mês'
              ) : (
                <MeasuredHelp
                  text={`${windowLabel(baseline)}: ${churnSource(baseline)}`}
                  edited={churn !== measuredChurn}
                  onReset={() => setTypedChurn(null)}
                />
              )
            }
            inputMode="decimal"
            suffix="%"
            placeholder="Ex.: 2"
            value={churnText}
            onChange={(e) => setTypedChurn(e.target.value)}
            error={
              churnInvalid
                ? churn !== null
                  ? 'O churn precisa ser menor que 100%.'
                  : 'Use o formato 2 ou 1,5.'
                : undefined
            }
          />
        </div>
      </section>

      <Metrics
        plan={plan}
        target={target}
        ticket={ticket}
        commission={commission}
        sales={sales}
        churn={churn}
      />

      <Projection plan={plan} target={target} sales={sales ?? 0} churn={churn} />
    </Page>
  );
}

/** A base medida muda com o fechamento do mês, não a cada minuto. */
const BASELINE_STALE_MS = 10 * 60 * 1000;

type MeasuredBaseline = Extract<WalletCalculator, { estado: 'medido' }>;

/** A base que dá os padrões dos campos — sem vínculo, não há padrão. */
function measuredBaseline(data: WalletCalculator | undefined): MeasuredBaseline | null {
  return data !== undefined && data.estado === 'medido' ? data : null;
}

/** "Sua carteira de julho a setembro de 2026" — os meses que a medição cobre. */
function windowLabel(baseline: MeasuredBaseline): string {
  const first = baseline.months[0]!.month;
  const last = baseline.months.at(-1)!.month;
  return `Sua carteira de ${monthName(first)} a ${periodLabel(last)}`;
}

/** "2 cancelamentos numa carteira de 6 carros, em média". */
function churnSource(baseline: MeasuredBaseline): string {
  const canceled = baseline.months.reduce((acc, m) => acc + m.canceled, 0);
  const portfolio = Math.round(
    baseline.months.reduce((acc, m) => acc + m.portfolio, 0) / baseline.months.length,
  );
  const lost =
    canceled === 0
      ? 'nenhum cancelamento'
      : plural(canceled, 'cancelamento', 'cancelamentos');
  return `${lost} numa carteira de ${plural(portfolio, 'carro', 'carros')}, em média`;
}

/**
 * A ajuda de um campo medido: de onde veio o número, e o caminho de volta a
 * ele quando a pessoa digitou outro.
 */
function MeasuredHelp({
  text,
  edited,
  onReset,
}: {
  text: string;
  edited: boolean;
  onReset: () => void;
}) {
  if (!edited) return <>{text}</>;
  return (
    <>
      {text} ·{' '}
      <button
        type="button"
        onClick={onReset}
        className={linkTextClasses('font-semibold')}
      >
        usar o medido
      </button>
    </>
  );
}

/** `11.11` → `11,11` — o texto que `parsePercent` lê de volta. */
function decimalText(value: number): string {
  return value.toLocaleString('pt-BR', { maximumFractionDigits: 2, useGrouping: false });
}

/** `2026-07` → "julho". */
function monthName(period: string): string {
  return new Intl.DateTimeFormat('pt-BR', { month: 'long', timeZone: 'UTC' }).format(
    new Date(`${period}-01T00:00:00Z`),
  );
}

/** `10` e `7,5` → número; vazio, negativo ou texto → `null`. */
function parsePercent(text: string): number | null {
  const clean = text.trim().replace('%', '').trim();
  if (!/^\d+([.,]\d+)?$/.test(clean)) return null;
  return Number(clean.replace(',', '.'));
}

/** `10` → `10%`, `7.5` → `7,5%`. */
function percentLabel(value: number): string {
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
}

function plural(n: number, one: string, many: string): string {
  return `${n.toLocaleString('pt-BR')} ${n === 1 ? one : many}`;
}

/** Os quatro números da calculadora. Sem conta, eles ficam com "—" e dizem o que falta. */
function Metrics({
  plan,
  target,
  ticket,
  commission,
  sales,
  churn,
}: {
  plan: RecurrencePlan | null;
  target: Money | null;
  ticket: Money | null;
  commission: number | null;
  sales: number | null;
  churn: number | null;
}) {
  const missing =
    target === null
      ? 'preencha a meta'
      : ticket === null
        ? 'preencha o ticket médio'
        : commission === null
          ? 'preencha a comissão'
          : 'confira os parâmetros';

  if (plan === null || target === null || ticket === null || commission === null) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric icon="pessoas" label="Clientes necessários" value="—" footer={missing} />
        <Metric
          icon="cartao"
          label="Recorrência por cliente"
          value="—"
          footer={missing}
        />
        <Metric icon="relogio" label="Meses para a meta" value="—" footer={missing} />
        <Metric
          icon="tendencia"
          label="Recorrência em 12 meses"
          value="—"
          footer={missing}
        />
      </div>
    );
  }

  /* O teto da carteira com churn: as vendas param de compensar os cancelamentos. */
  const ceiling =
    churn === null || churn === 0 || sales === null || sales === 0
      ? null
      : Math.floor(sales / (churn / 100));

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Metric
        icon="pessoas"
        label="Clientes necessários"
        value={plan.clientsNeeded.toLocaleString('pt-BR')}
        footer={`para ${format(target)}/mês`}
      />
      <Metric
        icon="cartao"
        label="Recorrência por cliente"
        value={format(plan.perClient)}
        footer={`${percentLabel(commission)} de ${format(ticket)}`}
      />
      <Metric
        icon="relogio"
        label="Meses para a meta"
        reached={plan.monthsToGoal !== null}
        value={
          plan.monthsToGoal !== null
            ? plural(plan.monthsToGoal, 'mês', 'meses')
            : sales === null || sales === 0
              ? '—'
              : 'Não chega'
        }
        footer={
          sales === null || sales === 0
            ? 'preencha as vendas por mês'
            : plan.monthsToGoal !== null
              ? `mantendo ${plural(sales, 'venda', 'vendas')}/mês`
              : `o churn segura a carteira em ${plural(ceiling ?? 0, 'cliente', 'clientes')}`
        }
      />
      <Metric
        icon="tendencia"
        label="Recorrência em 12 meses"
        value={format(plan.inTwelveMonths.recurrence)}
        footer={`${plural(Math.round(plan.inTwelveMonths.clients), 'cliente ativo', 'clientes ativos')}`}
      />
    </div>
  );
}

/**
 * Um número da calculadora: o ladrilho do ícone, o rótulo em caixa alta, o
 * número e uma linha de rodapé. `reached` pinta o cartão de verde — a meta tem
 * prazo —, e o texto do valor diz o mesmo, porque cor sozinha não é estado.
 */
function Metric({
  icon,
  label,
  value,
  footer,
  reached = false,
}: {
  icon: IconName;
  label: string;
  value: string;
  footer: string;
  reached?: boolean;
}) {
  return (
    <div
      className={cardClasses({
        padding: false,
        tone: reached ? 'sucesso' : 'neutro',
        className: 'flex items-start gap-3.5 rounded-2xl px-5 py-4',
      })}
    >
      <span
        className={cn(
          'grid size-10 shrink-0 place-items-center rounded-xl',
          reached ? 'bg-superficie text-sucesso' : 'bg-superficie-suave text-texto-suave',
        )}
      >
        <Icon nome={icon} size={20} />
      </span>
      <dl className="min-w-0">
        <dt className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-texto-suave">
          {label}
        </dt>
        <dd className="mt-1">
          <span
            className={cn(
              'block text-[1.375rem] font-bold leading-[1.15] tabular-nums',
              reached ? 'text-sucesso' : 'text-texto',
            )}
          >
            {value}
          </span>
          <span className="mt-1 block text-xs text-texto-suave">{footer}</span>
        </dd>
      </dl>
    </div>
  );
}

/** O cartão da projeção: o título, o mês da meta e a figura. */
function Projection({
  plan,
  target,
  sales,
  churn,
}: {
  plan: RecurrencePlan | null;
  target: Money | null;
  sales: number;
  churn: number | null;
}) {
  const month = plan?.monthsToGoal ?? null;
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Icon nome="tendencia" size={20} className="text-fase-laranja" />
          Projeção de crescimento
        </span>
      }
      descricao={
        plan === null ? (
          'preencha os parâmetros para ver a projeção'
        ) : month === null ? (
          'a meta não é atingida neste ritmo'
        ) : (
          <>
            meta atingida no{' '}
            <strong className="font-semibold text-sucesso">mês {month}</strong>
          </>
        )
      }
      actions={
        month !== null && (
          <Badge tone="sucesso">
            <Icon nome="confirmado" size={16} />
            Mês {month}
          </Badge>
        )
      }
    >
      {plan === null || target === null ? (
        <p className="py-10 text-center text-sm text-texto-suave">
          A projeção aparece quando a meta, o ticket médio e a comissão estiverem
          preenchidos.
        </p>
      ) : (
        <ProjectionChart plan={plan} target={target} sales={sales} churn={churn} />
      )}
    </Card>
  );
}

/** Altura da figura, e os recuos dos eixos. */
const HEIGHT = 260;
const PAD = { top: 12, right: 12, bottom: 28, left: 64 };

/**
 * A FIGURA — a recorrência mês a mês, com a área embaixo, e a meta tracejada.
 *
 * SVG próprio, e não o `Chart` da casa: o `Chart` não desenha linha de
 * referência, e a meta tracejada é o que a figura existe para mostrar — o mês
 * em que a curva a cruza.
 *
 * ## A dica acompanha o ponteiro (29/10/2026, pedido do dono)
 *
 * Passar o mouse sobre a figura escolhe o mês mais próximo na horizontal, e um
 * cartão segue o ponteiro com o que aquele mês prevê: a recorrência, os
 * clientes ativos, as vendas somadas até ali, os cancelamentos (com churn) e
 * quanto falta — ou sobra — para a meta. O cartão vira de lado perto da borda
 * para não sair da figura.
 */
function ProjectionChart({
  plan,
  target,
  sales,
  churn,
}: {
  plan: RecurrencePlan;
  target: Money;
  sales: number;
  churn: number | null;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState<{ index: number; x: number; y: number } | null>(
    null,
  );
  const gradient = `projecao${useId().replace(/:/g, '')}`;

  useEffect(() => {
    const el = box.current;
    if (el === null) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const points = plan.projection;
  const top = niceCeiling(Math.max(target, ...points.map((p) => p.recurrence)) * 1.1);
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const x = (i: number) =>
    PAD.left + (points.length === 1 ? plotW / 2 : (i / (points.length - 1)) * plotW);
  const y = (value: number) => PAD.top + plotH - (value / top) * plotH;

  const ticks = [0, 1, 2, 3, 4].map((i) => (top / 4) * i);
  /* Mês a mês até doze; depois, um rótulo a cada tantos, para não encavalar. */
  const every = Math.max(
    1,
    Math.ceil(points.length / Math.max(1, Math.floor(plotW / 64))),
  );

  const line = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.recurrence)}`)
    .join(' ');
  const area = `${line} L${x(points.length - 1)},${y(0)} L${x(0)},${y(0)} Z`;
  const goalY = y(target);

  const summary =
    plan.monthsToGoal === null
      ? `A recorrência chega a ${format(points[points.length - 1]!.recurrence)} no mês ${points.length}, abaixo da meta de ${format(target)}.`
      : `A recorrência passa a meta de ${format(target)} no mês ${plan.monthsToGoal}.`;

  /* O mês mais perto do ponteiro na horizontal — a faixa inteira é alvo, não só o ponto. */
  const onMove = (e: MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const raw = points.length === 1 ? 0 : ((mx - PAD.left) / plotW) * (points.length - 1);
    const index = Math.min(points.length - 1, Math.max(0, Math.round(raw)));
    setHover({ index, x: mx, y: my });
  };
  const hovered = hover === null ? null : points[hover.index]!;

  return (
    <div ref={box} className="relative w-full" style={{ height: HEIGHT }}>
      {width > 0 && (
        <svg
          width={width}
          height={HEIGHT}
          role="img"
          aria-label={summary}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        >
          <defs>
            <linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--cor-fase-laranja)" stopOpacity={0.22} />
              <stop offset="100%" stopColor="var(--cor-fase-laranja)" stopOpacity={0} />
            </linearGradient>
          </defs>

          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={PAD.left}
                x2={width - PAD.right}
                y1={y(t)}
                y2={y(t)}
                className="stroke-borda-suave"
                strokeDasharray="4 4"
              />
              <text
                x={PAD.left - 10}
                y={y(t)}
                dy="0.32em"
                textAnchor="end"
                fontSize={11}
                className="fill-texto-suave"
              >
                {compactMoney(t)}
              </text>
            </g>
          ))}

          {points.map((p, i) =>
            i % every === 0 || i === points.length - 1 ? (
              <text
                key={p.month}
                x={x(i)}
                y={HEIGHT - 8}
                textAnchor={
                  i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'
                }
                fontSize={11}
                className="fill-texto-suave"
              >
                Mês {p.month}
              </text>
            ) : null,
          )}

          <path d={area} fill={`url(#${gradient})`} />
          <path
            d={line}
            fill="none"
            strokeWidth={2}
            strokeLinejoin="round"
            className="stroke-fase-laranja"
          />

          <line
            x1={PAD.left}
            x2={width - PAD.right}
            y1={goalY}
            y2={goalY}
            strokeWidth={1.5}
            strokeDasharray="6 4"
            className="stroke-sucesso"
          />
          <text
            x={width - PAD.right}
            y={goalY - 6}
            textAnchor="end"
            fontSize={11}
            fontWeight={600}
            className="fill-sucesso"
          >
            Meta: {compactMoney(target)}
          </text>

          {hover !== null && hovered !== null && (
            <g className="pointer-events-none">
              <line
                x1={x(hover.index)}
                x2={x(hover.index)}
                y1={PAD.top}
                y2={PAD.top + plotH}
                className="stroke-borda"
              />
              <circle
                cx={x(hover.index)}
                cy={y(hovered.recurrence)}
                r={5}
                strokeWidth={2}
                className="fill-superficie stroke-fase-laranja"
              />
            </g>
          )}
          {/* A área que recebe o ponteiro: o plano inteiro, transparente. */}
          <rect
            x={PAD.left}
            y={PAD.top}
            width={plotW}
            height={plotH}
            fill="transparent"
          />
        </svg>
      )}
      {hover !== null && hovered !== null && (
        <MonthTip
          month={hovered}
          target={target}
          sales={sales}
          churn={churn}
          x={hover.x}
          y={hover.y}
          boxWidth={width}
        />
      )}
    </div>
  );
}

/** A largura do cartão da dica — fixa, para virar de lado antes de sair da figura. */
const TIP_WIDTH = 232;

/** O cartão que acompanha o ponteiro: o que aquele mês prevê, seguindo o mesmo ritmo. */
function MonthTip({
  month,
  target,
  sales,
  churn,
  x,
  y,
  boxWidth,
}: {
  month: RecurrencePlanMonth;
  target: Money;
  sales: number;
  churn: number | null;
  x: number;
  y: number;
  boxWidth: number;
}) {
  const clients = Math.round(month.clients);
  const sold = sales * month.month;
  const canceled = Math.max(0, sold - clients);
  const gap = (target - month.recurrence) as Money;
  const reached = gap <= 0;

  /* À direita do ponteiro; perto da borda, à esquerda. Na altura, sempre dentro da figura. */
  const left = x + 16 + TIP_WIDTH > boxWidth ? Math.max(0, x - 16 - TIP_WIDTH) : x + 16;
  const top = Math.min(Math.max(0, y - 24), HEIGHT - 150);

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute z-10 rounded-xl border border-borda bg-superficie p-3 text-xs shadow-flutuante"
      style={{ left, top, width: TIP_WIDTH }}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-texto">Mês {month.month}</span>
        {reached ? (
          <Badge tone="sucesso">meta atingida</Badge>
        ) : (
          <Badge tone="neutro">abaixo da meta</Badge>
        )}
      </div>
      <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1">
        <dt className="text-texto-suave">Recorrência prevista</dt>
        <dd className="text-right font-semibold tabular-nums text-fase-laranja">
          {format(month.recurrence)}
        </dd>
        <dt className="text-texto-suave">Clientes ativos</dt>
        <dd className="text-right tabular-nums text-texto">
          {clients.toLocaleString('pt-BR')}
        </dd>
        <dt className="text-texto-suave">Vendas somadas</dt>
        <dd className="text-right tabular-nums text-texto">
          {sold.toLocaleString('pt-BR')}
        </dd>
        {churn !== null && churn > 0 && (
          <>
            <dt className="text-texto-suave">Cancelamentos</dt>
            <dd className="text-right tabular-nums text-texto">
              {canceled.toLocaleString('pt-BR')}
            </dd>
          </>
        )}
        <dt className="text-texto-suave">
          {reached ? 'Acima da meta' : 'Falta para a meta'}
        </dt>
        <dd
          className={cn(
            'text-right font-semibold tabular-nums',
            reached ? 'text-sucesso' : 'text-texto',
          )}
        >
          {format(Math.abs(gap) as Money)}
        </dd>
      </dl>
    </div>
  );
}

/** O topo do eixo: o próximo 1, 2, 2,5 ou 5 × 10ⁿ, para os quatro degraus darem número redondo. */
function niceCeiling(value: number): number {
  if (value <= 0) return 100;
  const step = value / 4;
  const power = 10 ** Math.floor(Math.log10(step));
  const nice = [1, 2, 2.5, 5, 10].find((m) => m * power >= step)!;
  return nice * power * 4;
}
